import { Download, Upload } from 'lucide-react';
import * as React from 'react';

import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  toast,
} from '@/components/cargofl-ui';
import { PILL_CLASS } from '@/lib/badges';

function err(e: unknown): string | undefined {
  return e instanceof Error ? e.message : undefined;
}

export interface ParsedRows<T> {
  rows: T[];
  /** One message per rejected row, quoting the line number Excel shows. */
  errors: string[];
}

/** How one parsed entry reads in the preview, and whether it can be imported. */
export interface ImportSummary {
  /** The pill on the right — "3 goals · 100%", "5 KRAs · 95%". */
  text: string;
  /** The muted line under the name — an email, a department. Optional. */
  detail?: string;
  /**
   * Why this entry cannot be imported, when it cannot.
   *
   * Blocked entries are listed amber and skipped, never dropped from the preview: a
   * group that totals 95%, or names a KRA the library does not hold, is the row HR
   * most needs to see before pressing Import.
   */
  blocker?: string;
}

/**
 * The bulk-import dialog every "upload a spreadsheet" screen is built around —
 * KRAs, groups, and onboarding.
 *
 * Shared rather than copied, the same reason LibraryList is: the sheets differ only
 * in what a row means, and a second copy is where "creates only, never overwrites"
 * quietly stops being true on one of them.
 *
 * IMPORT ONLY EVER CREATES. An entry that already exists comes back as skipped, with
 * its name — re-importing last month's sheet must not revert wording somebody has
 * since fixed in the app. That is the same rule the seed lives under.
 */
export function BulkImportDialog<T extends { name: string }>({
  open,
  onOpenChange,
  title,
  description,
  templateHref,
  noun,
  parse,
  summarise,
  create,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  /** The sample sheet, served from `apps/web/public`. */
  templateHref: string;
  /** Singular, for the button and the toasts — "KRA", "group". */
  noun: string;
  /**
   * May be async: the people sheet names its reporting managers by email, and those
   * are looked up before the preview so a manager the directory does not hold is
   * shown as blocked rather than discovered row by row on import.
   */
  parse: (csv: string) => ParsedRows<T> | Promise<ParsedRows<T>>;
  summarise: (row: T) => ImportSummary;
  create: (row: T) => Promise<unknown>;
}) {
  const [parsed, setParsed] = React.useState<T[]>([]);
  const [errors, setErrors] = React.useState<string[]>([]);
  const [busy, setBusy] = React.useState(false);

  function reset() {
    setParsed([]);
    setErrors([]);
  }

  async function onFile(file: File | undefined) {
    reset();
    if (!file) return;
    setBusy(true);
    try {
      const result = await parse(await file.text());
      setParsed(result.rows);
      setErrors(result.errors);
    } catch (e) {
      setErrors([err(e) ?? 'That file could not be read.']);
    } finally {
      setBusy(false);
    }
  }

  const importable = parsed.filter((row) => summarise(row).blocker === undefined);

  async function onImport() {
    setBusy(true);
    let created = 0;
    const failed: string[] = [];

    // Sequential on purpose: the API refuses a duplicate name, and a burst of
    // parallel creates would race two rows of the same sheet against each other.
    for (const row of importable) {
      try {
        await create(row);
        created += 1;
      } catch (e) {
        failed.push(`${row.name}: ${err(e) ?? 'failed'}`);
      }
    }

    setBusy(false);
    // Says what it did rather than a bare success: "0 created, 5 already existed"
    // has to be distinguishable from "it worked".
    if (created > 0) {
      toast.success(`${created} ${noun}(s) imported`, {
        description: failed.length > 0 ? `${failed.length} skipped` : undefined,
      });
    }
    if (failed.length > 0) {
      toast.error(`${failed.length} ${noun}(s) not imported`, { description: failed.join(' · ') });
    }
    if (created > 0) {
      reset();
      onOpenChange(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <a
            href={templateHref}
            download
            className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
          >
            <Download className="size-4" />
            Download sample template (.csv)
          </a>

          <div className="grid gap-2">
            <Label htmlFor="importCsv">CSV file</Label>
            <Input
              id="importCsv"
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => void onFile(e.target.files?.[0])}
            />
          </div>

          {errors.length > 0 && (
            <div className="space-y-1 rounded-md border border-destructive/40 bg-destructive/5 p-3">
              <p className="text-xs font-medium text-destructive">{errors.length} row(s) skipped</p>
              {errors.slice(0, 8).map((message) => (
                <p key={message} className="text-xs text-muted-foreground">
                  {message}
                </p>
              ))}
              {errors.length > 8 && (
                <p className="text-xs text-muted-foreground">…and {errors.length - 8} more.</p>
              )}
            </div>
          )}

          {parsed.length > 0 && (
            <div className="max-h-64 space-y-2 overflow-y-auto rounded-md border p-3">
              {parsed.map((row, index) => {
                const summary = summarise(row);
                return (
                  // Index-keyed: the list is replaced wholesale on each file, never
                  // reordered, and two new joiners may share a name.
                  <div key={index} className="flex items-start justify-between gap-2 text-sm">
                    <div className="min-w-0">
                      <p className="truncate">{row.name}</p>
                      {summary.detail && (
                        <p className="truncate text-xs text-muted-foreground">{summary.detail}</p>
                      )}
                      {summary.blocker && (
                        <p className="text-xs text-muted-foreground">{summary.blocker}</p>
                      )}
                    </div>
                    <Badge
                      variant={summary.blocker ? 'soft-warning' : 'info'}
                      className={PILL_CLASS}
                    >
                      {summary.text}
                    </Badge>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button hint={"Import the rows shown above"}
            type="button"
            disabled={importable.length === 0 || busy}
            onClick={() => void onImport()}
          >
            {busy ? 'Importing…' : `Import ${importable.length} ${noun}(s)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The button that opens one.
 *
 * Teal-tinted rather than a plain outline, and the same in all three places, so
 * "there is a spreadsheet way to do this" is one recognisable thing rather than a
 * quiet button somebody has to already know about. It reads louder than the Create
 * New beside it on purpose: creating one is also offered inline from the search box,
 * and bulk import has no other entry point.
 *
 * Tokens only, never a hex — `primary` is the brand teal. → globals.css
 */
export function BulkImportButton({
  onClick,
  children,
}: {
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <Button
      variant="outline"
      className="gap-2 border-primary/40 bg-primary/5 font-semibold text-primary hover:bg-primary/10 hover:text-primary"
      onClick={onClick}
    >
      <Upload className="size-4" />
      {children}
    </Button>
  );
}
